/* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */
import { EncryptedData } from "@/types/export.types";
import { EXPORT_CONSTANTS } from "@/types/export.types";
import { CONSTANTS } from "@/config/constants";

export async function deriveKey(
  password: string,
  salt: Uint8Array
): Promise<CryptoKey> {
  console.log("[Crypto] deriveKey 开始");
  console.log("[Crypto] 密码长度:", password.length);
  console.log("[Crypto] Salt 长度:", salt.length);
  console.log("[Crypto] Salt 值:", Array.from(salt).map(b => b.toString(16).padStart(2, '0')).join(' '));

  const encoder = new TextEncoder();
  const passwordBuffer = encoder.encode(password);

  console.log("[Crypto] 密码 Buffer 长度:", passwordBuffer.length);

  try {
    const key = await crypto.subtle.importKey(
      "raw",
      passwordBuffer.buffer,
      { name: "PBKDF2" },
      false,
      ["deriveKey"]
    );
    console.log("[Crypto] 密钥导入成功");

    const derivedKey = await crypto.subtle.deriveKey(
      {
        name: "PBKDF2",
        salt: salt.buffer as ArrayBuffer,
        iterations: EXPORT_CONSTANTS.PBKDF2_ITERATIONS,
        hash: "SHA-256",
      },
      key,
      { name: "AES-GCM", length: EXPORT_CONSTANTS.KEY_LENGTH },
      false,
      ["encrypt", "decrypt"]
    );
    console.log("[Crypto] deriveKey 成功");
    return derivedKey;
  } catch (error) {
    console.error("[Crypto] deriveKey 失败:", error);
    throw error;
  }
}

export async function encryptData(
  data: Uint8Array,
  password: string
): Promise<EncryptedData> {
  console.log("[Crypto] encryptData 开始");
  console.log("[Crypto] 数据长度:", data.length);
  console.log("[Crypto] 数据前 20 字节:", Array.from(data.slice(0, 20)).map(b => b.toString(16).padStart(2, '0')).join(' '));

  const salt = crypto.getRandomValues(new Uint8Array(EXPORT_CONSTANTS.SALT_LENGTH));
  const iv = crypto.getRandomValues(new Uint8Array(EXPORT_CONSTANTS.IV_LENGTH));

  console.log("[Crypto] 生成的 Salt:", Array.from(salt).map(b => b.toString(16).padStart(2, '0')).join(' '));
  console.log("[Crypto] 生成的 IV:", Array.from(iv).map(b => b.toString(16).padStart(2, '0')).join(' '));

  try {
    const key = await deriveKey(password, salt);
    console.log("[Crypto] 密钥派生成功，开始加密");

    const encrypted = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv: iv.buffer as ArrayBuffer },
      key,
      data.buffer as ArrayBuffer
    );
    console.log("[Crypto] 加密成功，加密数据长度:", encrypted.byteLength);

    const encryptedArray = new Uint8Array(encrypted);
    const authTag = encryptedArray.slice(-EXPORT_CONSTANTS.AUTH_TAG_LENGTH);
    const ciphertext = encryptedArray.slice(0, -EXPORT_CONSTANTS.AUTH_TAG_LENGTH);

    console.log("[Crypto] 密文长度:", ciphertext.length);
    console.log("[Crypto] Auth Tag 长度:", authTag.length);
    console.log("[Crypto] encryptData 完成");

    return {
      encrypted: ciphertext,
      salt,
      iv,
      authTag,
    };
  } catch (error) {
    console.error("[Crypto] encryptData 失败:", error);
    console.error("[Crypto] 错误详情:", {
      name: error instanceof Error ? error.name : 'Unknown',
      message: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined
    });
    throw error;
  }
}

export async function decryptData(
  encryptedData: EncryptedData,
  password: string
): Promise<Uint8Array> {
  const { encrypted, salt, iv, authTag } = encryptedData;
  const key = await deriveKey(password, salt);

  const combined = new Uint8Array(encrypted.length + authTag.length);
  combined.set(encrypted);
  combined.set(authTag, encrypted.length);

  const decrypted = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: iv.buffer as ArrayBuffer },
    key,
    combined.buffer as ArrayBuffer
  );

  return new Uint8Array(decrypted);
}

export function validatePassword(password: string): boolean {
  return password.length >= CONSTANTS.MIN_PASSWORD_LENGTH;
}
